import React, { FC, useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import PageTitle from '../../../components/PageTitle'
import ConversationSidebar from './components/ConversationSidebar'
import ChatHeader from './components/ChatHeader'
import MessageList from './components/MessageList'
import MessageInput from './components/MessageInput'
import { useSSEChat } from './hooks/useSSEChat'
import { createConversation, getConversation } from '../../../services/cleo.service'
import { handlePromise } from '../../../utils/handlePromisse'
import { TOPBAR_DESKTOP, TOPBAR_MOBILE } from '../../../layouts/dashboard/DashboardNavbar'

const CLEO_SUGGESTIONS = [
    'Sugira ativos para melasma',
    'Qual o pH ideal para vitamina C?',
    'Posso usar retinóide em gestante?',
]

export const CleoPage: FC = () => {
    const [activeConversationId, setActiveConversationId] = useState<number | null>(null)
    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

    const onComplete = useCallback(() => {
        setRefreshTrigger(prev => prev + 1)
        if (activeConversationId) {
            loadConversation(activeConversationId)
        }
    }, [activeConversationId])

    const { messages, setMessages, status, sendMessage, cancel } = useSSEChat(onComplete)

    const loadConversation = useCallback(async (id: number) => {
        const [result, error] = await handlePromise(getConversation(id))
        if (error || !result) return
        setMessages(
            (result.data.messages || []).map((m: any) => ({
                id: m.id,
                role: m.role,
                content: m.content,
                createdAt: m.createdAt,
            }))
        )
    }, [setMessages])

    const handleSelectConversation = useCallback((id: number) => {
        cancel()
        setActiveConversationId(id)
        loadConversation(id)
    }, [cancel, loadConversation])

    const handleNewConversation = useCallback(() => {
        cancel()
        setActiveConversationId(null)
        setMessages([])
    }, [cancel, setMessages])

    const handleSend = useCallback(async (content: string) => {
        let conversationId = activeConversationId

        if (!conversationId) {
            const [result, error] = await handlePromise(createConversation())
            if (error || !result) return
            conversationId = result.data.id
            setActiveConversationId(conversationId)
            setRefreshTrigger(prev => prev + 1)
        }

        sendMessage(conversationId!, content)
    }, [activeConversationId, sendMessage])

    const isBusy = status === 'streaming' || status === 'tool_processing'

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                height: { xs: `calc(100vh - ${TOPBAR_MOBILE}px)`, lg: `calc(100vh - ${TOPBAR_DESKTOP}px)` },
                overflow: 'hidden',
            }}
        >
            <PageTitle title="Cleo" />

            <ConversationSidebar
                activeId={activeConversationId}
                onSelect={handleSelectConversation}
                onNew={handleNewConversation}
                refreshTrigger={refreshTrigger}
                mobileOpen={mobileMenuOpen}
                onMobileClose={() => setMobileMenuOpen(false)}
            />

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                <ChatHeader onMenuClick={() => setMobileMenuOpen(true)} />
                <MessageList messages={messages} onAction={handleSend} />
                <MessageInput
                    onSend={handleSend}
                    disabled={isBusy}
                    suggestions={messages.length === 0 ? CLEO_SUGGESTIONS : []}
                />
            </Box>
        </Box>
    )
}
